// BlockedMessageContent.tsx
// Content component for blocked notification message (to be used with useModal)

import React, { useState } from "react";
import { Button } from "components/Button";
import { BTN_SIZE, BTN_VARIANTS } from "constants/components";
import { getEnableInstructions } from "@/services/push.service";
import { NOTIFICATION, PERMISSION_STATUS } from "@/constants/notification";
import { Image } from "@/components/Image";
import notificationIcon from "@/assets/images/notification_bell.svg";
import parse from "html-react-parser";
import styles from "./BlockedMessageContent.module.scss";

interface BlockedMessageContentProps {
  onDismissForever: () => void;
  onRetry: () => Promise<void>;
  isChecking?: boolean;
  permissionStatus?:
    | typeof PERMISSION_STATUS.BLOCKED
    | typeof PERMISSION_STATUS.UNSUPPORTED;
}

/**
 * Content showing when notifications are blocked or unsupported
 * To be rendered inside useModal()
 */
export const BlockedMessageContent: React.FC<BlockedMessageContentProps> = ({
  onDismissForever,
  onRetry,
  isChecking = false,
  permissionStatus = PERMISSION_STATUS.BLOCKED,
}) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const instructions = getEnableInstructions();

  const isUnsupported = permissionStatus === PERMISSION_STATUS.UNSUPPORTED;

  const title = isUnsupported
    ? NOTIFICATION.BLOCKED_MESSAGE.UNSUPPORTED.TITLE
    : NOTIFICATION.BLOCKED_MESSAGE.BLOCKED.TITLE;

  const description = isUnsupported
    ? NOTIFICATION.BLOCKED_MESSAGE.UNSUPPORTED.DESCRIPTION
    : NOTIFICATION.BLOCKED_MESSAGE.BLOCKED.DESCRIPTION;

  return (
    <div className={styles.blocked_message_content}>
      {/* Icon */}
      <div className={styles.blocked_message_icon}>
        <Image source={notificationIcon} />
      </div>

      {/* Title */}
      <h2 className={styles.blocked_message_title}>{title}</h2>

      {/* Description */}
      <div className={styles.blocked_message_description}>
        {parse(description)}

        {/* Instructions Section (Expandable) - Only for blocked, not unsupported */}
        {!isUnsupported && (
          <>
            {!showInstructions ? (
              <button
                className={styles.show_instructions_button}
                onClick={() => setShowInstructions(true)}
                type="button"
              >
                {NOTIFICATION.BLOCKED_MESSAGE.BLOCKED.SHOW_INSTRUCTIONS_TEXT}
              </button>
            ) : (
              <div className={styles.instructions_section}>
                <h3 className={styles.instructions_title}>
                  {instructions.title}
                </h3>
                <ol className={styles.instructions_list}>
                  {instructions.steps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
                <p className={styles.instructions_note}>
                  {NOTIFICATION.BLOCKED_MESSAGE.BLOCKED.INSTRUCTIONS_NOTE}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Buttons */}
      <div className={styles.blocked_message_buttons}>
        {!isUnsupported && (
          <Button
            type={BTN_VARIANTS.PRIMARY}
            size={BTN_SIZE.LARGE}
            onClick={onRetry}
            disabled={isChecking}
          >
            {isChecking
              ? NOTIFICATION.BLOCKED_MESSAGE.BLOCKED.RETRY_BUTTON_TEXT_CHECKING
              : NOTIFICATION.BLOCKED_MESSAGE.BLOCKED.RETRY_BUTTON_TEXT}
          </Button>
        )}
        <Button
          type={BTN_VARIANTS.SECONDARY}
          size={BTN_SIZE.LARGE}
          onClick={onDismissForever}
        >
          {NOTIFICATION.BLOCKED_MESSAGE.DISMISS_BUTTON_TEXT}
        </Button>
      </div>
    </div>
  );
};
